import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import {
  listAuditActions,
  listAuditLog,
  type AuditLogEntry,
  type AuditList,
} from '../../lib/api/admin';
import { fmtDateTime } from '../../lib/format';
import { FilterField } from './FilterField';
import { Pagination } from './Pagination';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/**
 * Activity tab — paginated audit log viewer. The list endpoint accepts an
 * action prefix (so `admin.` covers every admin event) plus optional entity /
 * entityId / actor / date filters. Rows expand to show the parsed before/after
 * JSON; for an unchanged-shape event we just show `newData`.
 */
export function ActivityTab() {
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [entityIdFilter, setEntityIdFilter] = useState('');
  const [actorIdFilter, setActorIdFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);

  const [data, setData] = useState<AuditList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actions, setActions] = useState<string[]>([]);

  // Distinct-action list is cheap and changes only when something new is
  // logged for the first time — pull once on mount and leave it alone. A new
  // action that appears mid-session shows up in the dropdown on next reload.
  useEffect(() => {
    const controller = new AbortController();
    listAuditActions({ signal: controller.signal })
      .then(setActions)
      .catch(() => {
        // Non-fatal — the dropdown just stays empty.
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    listAuditLog(
      {
        action: actionFilter || undefined,
        entity: entityFilter || undefined,
        entityId: entityIdFilter || undefined,
        actorUserId: actorIdFilter || undefined,
        fromDate: fromDate ? new Date(fromDate).toISOString() : undefined,
        toDate: toDate ? new Date(toDate).toISOString() : undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      { signal: controller.signal },
    )
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(
          err instanceof ApiError
            ? `Couldn't load activity (${err.status}).`
            : "Couldn't load activity.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    actionFilter,
    entityFilter,
    entityIdFilter,
    actorIdFilter,
    fromDate,
    toDate,
    page,
    pageSize,
    reloadKey,
  ]);

  const total = data?.total ?? 0;
  const entries = data?.entries ?? [];

  const resetFilters = (): void => {
    setActionFilter('');
    setEntityFilter('');
    setEntityIdFilter('');
    setActorIdFilter('');
    setFromDate('');
    setToDate('');
    setPage(1);
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-3">
        <FilterField label="Action">
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          >
            <option value="">All</option>
            <option value="admin.">admin.* (any admin action)</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Entity">
          <input
            type="text"
            value={entityFilter}
            onChange={(e) => {
              setEntityFilter(e.target.value);
              setPage(1);
            }}
            placeholder="users, weekly_rules…"
            className="w-44 rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          />
        </FilterField>
        <FilterField label="Entity ID">
          <input
            type="text"
            value={entityIdFilter}
            onChange={(e) => {
              setEntityIdFilter(e.target.value);
              setPage(1);
            }}
            placeholder="userId, weekNumber…"
            className="w-52 rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          />
        </FilterField>
        <FilterField label="Actor user ID">
          <input
            type="text"
            value={actorIdFilter}
            onChange={(e) => {
              setActorIdFilter(e.target.value);
              setPage(1);
            }}
            placeholder="who did it"
            className="w-52 rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          />
        </FilterField>
        <FilterField label="From">
          <input
            type="datetime-local"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          />
        </FilterField>
        <FilterField label="To">
          <input
            type="datetime-local"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          />
        </FilterField>
        <button
          type="button"
          onClick={resetFilters}
          className="self-end rounded-full border border-white/25 px-4 py-2 text-xs uppercase tracking-wide text-white/80 transition hover:bg-white/10"
        >
          Reset
        </button>
      </div>

      {error && <p className="text-white/80">{error}</p>}

      <div
        aria-busy={loading}
        className={`overflow-x-auto rounded-xl border border-white/15 bg-black/20 transition-opacity ${
          loading ? 'opacity-60' : 'opacity-100'
        }`}
      >
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/55">
              <Th>When</Th>
              <Th>Action</Th>
              <Th>Entity</Th>
              <Th>Actor</Th>
              <Th>Target</Th>
              <Th>Details</Th>
            </tr>
          </thead>
          <tbody>
            {loading && entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-white/50">
                  Loading activity…
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-white/50">
                  No matching events.
                </td>
              </tr>
            ) : (
              entries.map((e) => <AuditRow key={e.logId} entry={e} />)
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disabled={loading}
        itemLabel="events"
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDiff = entry.previousData !== null || entry.newData !== null;
  // Pretty-printed JSON is memoized so toggling expand/collapse on a large
  // diff doesn't reformat every render.
  const previousJson = useMemo(
    () => (entry.previousData == null ? null : JSON.stringify(entry.previousData, null, 2)),
    [entry.previousData],
  );
  const newJson = useMemo(
    () => (entry.newData == null ? null : JSON.stringify(entry.newData, null, 2)),
    [entry.newData],
  );

  return (
    <>
      <tr className="border-b border-white/5 align-top text-white/85">
        <td className="px-4 py-3 text-xs text-white/60">{fmtDateTime(entry.createdAt)}</td>
        <td className="px-4 py-3">
          <span className="font-mono text-xs text-white">{entry.action}</span>
        </td>
        <td className="px-4 py-3 text-xs text-white/70">
          {entry.entity}
          {entry.entityId ? (
            <div className="text-white/45 [overflow-wrap:anywhere]">{entry.entityId}</div>
          ) : null}
        </td>
        <td className="px-4 py-3 text-xs">
          <div className="text-white/85 [overflow-wrap:anywhere]">{entry.actorEmail ?? '—'}</div>
          {entry.userId && (
            <div className="text-white/40 [overflow-wrap:anywhere]">{entry.userId}</div>
          )}
        </td>
        <td className="px-4 py-3 text-xs">
          <div className="text-white/85 [overflow-wrap:anywhere]">{entry.targetEmail ?? '—'}</div>
        </td>
        <td className="px-4 py-3 text-xs">
          {hasDiff ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="rounded-full border border-white/25 px-3 py-1 text-white/80 transition hover:bg-white/10"
            >
              {expanded ? 'Hide' : 'View'}
            </button>
          ) : (
            <span className="text-white/40">—</span>
          )}
        </td>
      </tr>
      {expanded && hasDiff && (
        <tr className="border-b border-white/5 bg-black/30">
          <td colSpan={6} className="px-4 py-3">
            <div className="grid gap-3 lg:grid-cols-2">
              {previousJson != null && (
                <JsonBlock label="Previous" tone="warn">
                  {previousJson}
                </JsonBlock>
              )}
              {newJson != null && (
                <JsonBlock label="New" tone="ok">
                  {newJson}
                </JsonBlock>
              )}
              {entry.ipAddress || entry.requestId ? (
                <div className="lg:col-span-2 text-xs text-white/50">
                  {entry.ipAddress && <span>IP: {entry.ipAddress}</span>}
                  {entry.ipAddress && entry.requestId ? ' · ' : ''}
                  {entry.requestId && <span>Request: {entry.requestId}</span>}
                </div>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function JsonBlock({ label, tone, children }: { label: string; tone: 'warn' | 'ok'; children: string }) {
  const toneCls = tone === 'warn' ? 'border-[#c0344f]/40 text-[#ffd1db]' : 'border-[#00ff8b]/30 text-[#caffe2]';
  return (
    <div className={`rounded-md border ${toneCls} bg-black/40 p-3`}>
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide opacity-80">{label}</div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-snug">
        {children}
      </pre>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-4 py-2 font-heading text-xs uppercase tracking-wide">{children}</th>;
}

